import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { Selection } from 'prosemirror-state';
import { TableMap } from '@atlaskit/editor-tables/table-map';
import { findTable } from '@atlaskit/editor-tables/utils';
import { AddColumnStep } from '@atlaskit/adf-schema/steps';
import { setMeta } from './metadata';
import { splitCellsInColumns } from './split';
var deleteColumnsCustomStep = function deleteColumnsCustomStep(rect) {
  return function (tr) {
    var table = findTable(tr.selection);
    if (!table) {
      return tr;
    }

    // Need to split all the merge in the ranges (this is the current behaviour)
    // Maybe is better to split only the last column?
    // TODO: After talking with Roto about this behaviour, he likes when we dont split the columns, I am keeping this for consistency of the current implementation.
    splitCellsInColumns(tr, table.pos, rect.left, rect.right);

    // Delete the columns
    var mapStart = tr.mapping.maps.length;
    var originalDoc = tr.doc;
    var deletedColumns = [];
    for (var i = rect.left; i < rect.right; i++) {
      var step = AddColumnStep.create(originalDoc, table.pos, i, true);
      deletedColumns.push(i);
      tr.step(step.map(tr.mapping.slice(mapStart)));
    }
    var tablePosResult = tr.mapping.mapResult(table.pos);
    if (tablePosResult.deleted) {
      var pos = Math.min(tablePosResult.pos, tr.doc.nodeSize - 1);
      tr.setSelection(Selection.near(tr.doc.resolve(pos)));
    } else {
      var newTable = tr.doc.nodeAt(tablePosResult.pos);
      if (newTable) {
        var cursorPos = getNextCursorPos(newTable, deletedColumns);
        tr.setSelection(Selection.near(tr.doc.resolve(table.pos + cursorPos)));
      }
    }
    return tr;
  };
};
var deleteColumnsLegacy = function deleteColumnsLegacy(rect) {
  return function (tr) {
    var table = findTable(tr.selection);
    if (!table) {
      return tr;
    }
    var columnsToDelete = [];
    for (var i = rect.left; i < rect.right; i++) {
      columnsToDelete.push(i);
    }
    if (!columnsToDelete.length) {
      return tr;
    }
    var map = TableMap.get(table.node);
    var rows = [];
    var seen = {};
    var deletedCells = {};
    for (var rowIndex = 0; rowIndex < map.height; rowIndex++) {
      var rowCells = [];
      var row = table.node.child(rowIndex);
      var _loop = function _loop(colIndex) {
        var cellPos = map.map[rowIndex * map.width + colIndex];
        var cell = table.node.nodeAt(cellPos);
        if (!cell) {
          return "continue";
        }
        var cellsInColumn = map.cellsInRect({
          left: colIndex,
          top: 0,
          right: colIndex + 1,
          bottom: map.height
        });
        if (columnsToDelete.indexOf(colIndex) === -1) {
          // decrement colspans for col-spanning cells that overlap deleted columns
          if (cellsInColumn.indexOf(cellPos) > -1 && !seen[cellPos]) {
            var overlappingCols = 0;
            columnsToDelete.forEach(function (colIndexToDelete) {
              if (colIndex < colIndexToDelete && cell.attrs.colspan + colIndex - 1 >= colIndexToDelete) {
                overlappingCols += 1;
              }
            });
            if (overlappingCols > 0) {
              var attrs = _objectSpread(_objectSpread({}, cell.attrs), {}, {
                colspan: cell.attrs.colspan - overlappingCols
              });
              if (cell.attrs.colwidth) {
                var minColIndex = Math.min.apply(Math, columnsToDelete);
                var pos = minColIndex > 0 ? minColIndex - map.colCount(cellPos) : 0;
                var colwidth = cell.attrs.colwidth.slice() || [];
                colwidth.splice(pos, overlappingCols);
                attrs.colwidth = colwidth;
              }
              var newCell = cell.type.createChecked(attrs, cell.content, cell.marks);
              rowCells.push(newCell);
              seen[cellPos] = true;
              return "continue";
            }
          } else if (deletedCells[cellPos]) {
            // if we're removing a col-spanning cell, we need to add missing cells to columns to the right
            var _attrs = _objectSpread(_objectSpread({}, cell.attrs), {}, {
              colspan: 1,
              rowspan: 1
            });
            if (cell.attrs.colwidth) {
              var _pos = colIndex > 0 ? colIndex - map.colCount(cellPos) : 0;
              _attrs.colwidth = cell.attrs.colwidth.slice().splice(_pos, 1);
            }
            var _newCell = cell.type.createChecked(_attrs, cell.type.schema.nodes.paragraph.createChecked(), cell.marks);
            rowCells.push(_newCell);
            return "continue";
          }

          // normal cells that we want to keep
          if (!seen[cellPos]) {
            seen[cellPos] = true;
            rowCells.push(cell);
          }
        } else if (cellsInColumn.indexOf(cellPos) > -1) {
          deletedCells[cellPos] = true;
        }
      };
      for (var colIndex = 0; colIndex < map.width; colIndex++) {
        var _ret = _loop(colIndex);
        if (_ret === "continue") continue;
      }
      if (rowCells.length) {
        rows.push(row.type.createChecked(row.attrs, rowCells, row.marks));
      }
    }
    if (!rows.length) {
      return setMeta({
        type: 'DELETE_COLUMNS',
        problem: 'EMPTY_TABLE'
      })(tr);
    }
    var newTable = table.node.type.createChecked(table.node.attrs, rows, table.node.marks);
    var fixedTable = fixRowSpans(newTable);
    if (fixedTable === null) {
      return setMeta({
        type: 'DELETE_COLUMNS',
        problem: 'FIX_ROWSPANS'
      })(tr);
    }
    var cursorPos = getNextCursorPos(newTable, columnsToDelete);
    return setMeta({
      type: 'DELETE_COLUMNS'
    })(tr.replaceWith(table.pos, table.pos + table.node.nodeSize, fixedTable)
    // move cursor to the left of the deleted columns if possible, otherwise - to the first column
    .setSelection(Selection.near(tr.doc.resolve(table.pos + cursorPos))));
  };
};
function getNextCursorPos(table, deletedColumns) {
  var minColumn = Math.min.apply(Math, _toConsumableArray(deletedColumns));
  var nextColumnWithCursor = minColumn > 0 ? minColumn - 1 : 0;
  var map = TableMap.get(table);
  return map.map[nextColumnWithCursor];
}

// returns an array of numbers, each number indicates the minimum rowSpan in each row
function getMinRowSpans(table) {
  var minRowSpans = [];
  for (var rowIndex = 0; rowIndex < table.childCount; rowIndex++) {
    var rowSpans = [];
    var row = table.child(rowIndex);
    for (var colIndex = 0; colIndex < row.childCount; colIndex++) {
      var cell = row.child(colIndex);
      rowSpans.push(cell.attrs.rowspan);
    }
    minRowSpans[rowIndex] = Math.min.apply(Math, rowSpans);
  }
  return minRowSpans;
}
function fixRowSpans(table) {
  var map = TableMap.get(table);
  var minRowSpans = getMinRowSpans(table);
  if (!minRowSpans.some(function (rowspan) {
    return rowspan > 1;
  })) {
    return table;
  }
  var rows = [];
  for (var rowIndex = 0; rowIndex < map.height; rowIndex++) {
    var row = table.child(rowIndex);
    if (minRowSpans[rowIndex] === 1) {
      rows.push(row);
    } else {
      var rowCells = [];
      for (var colIndex = 0; colIndex < row.childCount; colIndex++) {
        var cell = row.child(colIndex);
        var rowspan = cell.attrs.rowspan - minRowSpans[rowIndex] + 1;
        if (rowspan < 1) {
          return null;
        }
        var newCell = cell.type.createChecked(_objectSpread(_objectSpread({}, cell.attrs), {}, {
          rowspan: rowspan
        }), cell.content, cell.marks);
        rowCells.push(newCell);
      }
      rows.push(row.type.createChecked(row.attrs, rowCells, row.marks));
    }
  }
  if (!rows.length) {
    return null;
  }
  return table.type.createChecked(table.attrs, rows, table.marks);
}
export var deleteColumns = function deleteColumns(rect) {
  var allowAddColumnCustomStep = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  return function (tr) {
    if (allowAddColumnCustomStep) {
      return deleteColumnsCustomStep(rect)(tr);
    }
    return deleteColumnsLegacy(rect)(tr);
  };
};